import React, { useState } from "react";
import "./incometaxcalculator.css";
import { API_BASE_URL, NGROK_URL } from "../../utils/const";
import axios from "axios";

// Schema-driven field rendering
const formSchema = [
  {
    type: "select",
    name: "incomeYear",
    label: "Income year *",
    required: true,
    options: [
      "",
      "2013-14",
      "2014-15",
      "2015-16",
      "2016-17",
      "2017-18",
      "2018-19",
      "2019-20",
      "2020-21",
      "2021-22",
      "2022-23",
      "2023-24",
    ],
  },
  {
    type: "radio",
    name: "residencyStatus",
    label: "Your residency status for the selected income year *",
    required: true,
    options: [
      { value: "resident", label: "Australian resident for the full year" },
      { value: "foreign", label: "Foreign resident for the full year" },
      { value: "part-year", label: "Part year Australian resident" },
    ],
  },
  {
    type: "select",
    name: "partTimeDuration",
    label: "Select Duration *",
    required: true,
    condition: (formData) => formData.residencyStatus === "part-year",
    options: ["", ...Array.from({ length: 11 }, (_, i) => `${i + 1}`)],
  },
  {
    type: "radio",
    name: "hadSpouse",
    label: "Did you have a spouse on 30 June 2024? *",
    required: true,
    condition: (formData) => formData.incomeYear !== "",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    type: "checkboxGroup",
    label:
      "Select the items that apply to you to include in your income tax estimate",
    condition: (formData) => formData.incomeYear !== "",
    checkboxes: [
      {
        name: "carriedForwardLosses",
        label: "You have carried forward losses",
      },
      { name: "hadIncomeLoan", label: "You had an Income Contingent Loan" },
      { name: "hadTaxOffsets", label: "You had tax offsets" },
      {
        name: "isWorkingHolidayMaker",
        label: "You are a working holiday maker",
      },
    ],
  },
  {
    type: "radio",
    name: "nonDiscCountry",
    label: "Are you from a non-discriminatory article country(Opens help)? * Tooltip If you are from a non-discriminatory article (NDA) country and an Australian resident for tax purposes, select yes.",
    required: true,
    condition: (formData) => formData.isWorkingHolidayMaker ===true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
];

const IncomeTaxCalculator = () => {
  const [formData, setFormData] = useState({
    incomeYear: "",
    residencyStatus: "resident",
    hadSpouse: "no",
    carriedForwardLosses: false,
    hadIncomeLoan: false,
    hadTaxOffsets: false,
    isWorkingHolidayMaker: false,
    partTimeDuration: "",
    nonDiscCountry:"",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [incomeRecord, setIncomeRecord] = useState({
    grossIncome: "",
    taxWithheld: "",
    deductions: "",
  });
  const [isAddingIncome, setIsAddingIncome] = useState(false);
  const [hasSubmittedIncome, setHasSubmittedIncome] = useState(false);

  const [incomeTests, setIncomeTests] = useState({
    fringeBenefits: '',
    fringeBenefitsNonExempt: '',
    employerSuper: '',
    investmentLoss: '',
    rentalLoss: '',
    personalSuper: '',
    familyTrustTax: '',
    superLumpSum: '',
    foreignIncome: ''
  });
  const [isAddingTests, setIsAddingTests] = useState(false);
  const [hasSubmittedTests, setHasSubmittedTests] = useState(false);

  const [spouseDetails, setSpouseDetails] = useState({
    taxableIncome: "",
    fringeBenefits: "",
    employerSuper: "",
    investmentLoss: "",
    rentalLoss: "",
    personalSuper: "",
    trustIncome: "",
    familyTrustTax: "",
    superLumpSum: "",
    foreignIncome: "",
  });
  const [isAddingSpouse, setIsAddingSpouse] = useState(false);
  const [hasSubmittedSpouse, setHasSubmittedSpouse] = useState(false);

  const [taxOffsets, setTaxOffsets] = useState({
    zoneOverseasForces: '',
    lifeAssuranceBonuses: '',
    seniorsAndPensioners: '',
    australianSuper: '',
    foreignIncome: '',
    invalidCarer: '',
    landcareWater: '',
    nationalRental: '',
    earlyStageVenture: '',
    earlyStageInvestor: '',
    otherRefundable: '',
    spouseSuper: ''
  });
  const [isAddingOffsets, setIsAddingOffsets] = useState(false);
  const [hasSubmittedOffsets, setHasSubmittedOffsets] = useState(false);

  const [otherAmounts, setOtherAmounts] = useState({
    foreignCredits: '',
    lossesCarriedForward: '',
    earlyPaymentsCredit: ''
  });
  const [isAddingOther, setIsAddingOther] = useState(false);
  const [hasSubmittedOther, setHasSubmittedOther] = useState(false);

  const [medicareDetails, setMedicareDetails] = useState({
    dependentChildren: '',
    medicareLevyAmount: '',
    hasPrivateInsurance: '',
    mlsSurchargeDays: ''
  });

  const [insurancePolicies, setInsurancePolicies] = useState([]);
  const [isAddingMedicare, setIsAddingMedicare] = useState(false);
  const [hasSubmittedMedicare, setHasSubmittedMedicare] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState({
    premium: '',
    rebateReceived: '',
    benefitCode: '',
    claimingForSpouse: false
  });


// Add this new state for working holiday maker income
const [holidayMakerIncome, setHolidayMakerIncome] = useState({
  grossIncome: "",
  taxWithheld: "",
  deductions: "",
});
const [isAddingHolidayIncome, setIsAddingHolidayIncome] = useState(false);
const [hasSubmittedHolidayIncome, setHasSubmittedHolidayIncome] = useState(false);

  const getFilledSections = () => {
    const sections = [];
    
    // Section 1: Income and deductions
    if (Object.values(incomeRecord).some(value => value !== '')) {
      sections.push(1);
    }
    if (formData.isWorkingHolidayMaker && Object.values(holidayMakerIncome).some(value => value !== '')) {
      sections.push(2); // or whatever section number you want to assign
  }
    
    // Section 2: Income tests
    if (Object.values(incomeTests).some(value => value !== '')) {
      sections.push(3);
    }
    
    // Section 3: Spouse details
    if (Object.values(spouseDetails).some(value => value !== '')) {
      sections.push(4);
    }
    
    // Section 4: Tax offsets
    if (Object.values(taxOffsets).some(value => value !== '')) {
      sections.push(5);
    }
    
    // Section 5: Other amounts
    if (Object.values(otherAmounts).some(value => value !== '')) {
      sections.push(6);
    }
    
    // Section 6: Medicare and private health
    if (Object.values(medicareDetails).some(value => value !== '') || insurancePolicies.length > 0) {
      sections.push(7);
    }
    
    return sections;
  };
  

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleIncomeChange = (field, value) => {
    setIncomeRecord((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTestsChange = (field, value) => {
    setIncomeTests(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSpouseChange = (field, value) => {
    setSpouseDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOffsetsChange = (field, value) => {
    setTaxOffsets(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOtherChange = (field, value) => {
    setOtherAmounts(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMedicareChange = (field, value) => {
    setMedicareDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddEditIncome = () => {
    setIsAddingIncome(true);
    if (hasSubmittedIncome) {
      setIncomeRecord(incomeRecord);
    }
  };

  const handleAddEditTests = () => {
    setIsAddingTests(true);
  };

  const handleAddEditSpouse = () => {
    setIsAddingSpouse(true);
  };

  const handleAddEditOffsets = () => {
    setIsAddingOffsets(true);
    if (hasSubmittedOffsets) {
      setTaxOffsets(taxOffsets);
    }
  };

  const handleAddEditOther = () => {
    setIsAddingOther(true);
    if (hasSubmittedOther) {
      setOtherAmounts(otherAmounts);
    }
  };

  const handleAddEditMedicare = () => {
    setIsAddingMedicare(true);
  };

  const handleSaveIncome = () => {
    setIsAddingIncome(false);
    setHasSubmittedIncome(true);
  };

  const handleSaveTests = () => {
    setIsAddingTests(false);
    setHasSubmittedTests(true);
  };

  const handleSaveSpouse = () => {
    setIsAddingSpouse(false);
    setHasSubmittedSpouse(true);
  };

  const handleSaveOffsets = () => {
    setIsAddingOffsets(false);
    setHasSubmittedOffsets(true);
  };

  const handleSaveOther = () => {
    setIsAddingOther(false);
    setHasSubmittedOther(true);
  };

  const handleSaveMedicare = () => {
    setIsAddingMedicare(false);
    setHasSubmittedMedicare(true);
  };

  const handleAddPolicy = () => {
    setShowPolicyForm(true);
  };

  const handlePolicyChange = (field, value) => {
    setCurrentPolicy(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePolicy = () => {
    if (currentPolicy.premium && currentPolicy.rebateReceived && currentPolicy.benefitCode) {
      setInsurancePolicies(prev => [...prev, { ...currentPolicy, id: Date.now() }]);
      setCurrentPolicy({
        premium: '',
        rebateReceived: '',
        benefitCode: '',
        claimingForSpouse: false
      });
      setShowPolicyForm(false);
    }
  };

  const handleEditPolicy = (policyId) => {
    const policy = insurancePolicies.find(p => p.id === policyId);
    if (policy) {
      setCurrentPolicy(policy);
      setShowPolicyForm(true);
    }
  };

  const handleDeletePolicy = (policyId) => {
    setInsurancePolicies(prev => prev.filter(p => p.id !== policyId));
  };

  const handleHolidayIncomeChange = (field, value) => {
    setHolidayMakerIncome(prev => ({
        ...prev,
        [field]: value
    }));
};

const handleAddEditHolidayIncome = () => {
    setIsAddingHolidayIncome(true);
    if (hasSubmittedHolidayIncome) {
        setHolidayMakerIncome(holidayMakerIncome);
    }
};

const handleSaveHolidayIncome = () => {
    setIsAddingHolidayIncome(false);
    setHasSubmittedHolidayIncome(true);
};

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
  
    // Transform income year format (e.g., "2018–2019" to "2018-19")
    const targetYear = formData.incomeYear ??null
  
    // Transform residency status
    const residencyStatus = {
      'resident': 'full',
      'foreign': 'foreign',
      'part-year': 'part'
    }[formData.residencyStatus];
  
    // Transform tax item positions based on checkboxes
    const taxItemPositions = [];
    if (formData.carriedForwardLosses) taxItemPositions.push(1);
    if (formData.hadIncomeLoan) taxItemPositions.push(3);
    if (formData.hadTaxOffsets) taxItemPositions.push(5);
  
    // Transform phi records
    const phiRecords = insurancePolicies.map(policy => ({
      premium: Number(policy.premium),
      rebate: Number(policy.rebateReceived),
      code: policy.benefitCode,
      for_spouse: policy.claimingForSpouse
    }));
  
    const payload = {
      target_year: targetYear,
      residency_status: residencyStatus,
      months_resident: formData.residencyStatus === 'part-year' ? Number(formData.partTimeDuration) : null,
      has_spouse: formData.hadSpouse,
      tax_item_positions: taxItemPositions,
      is_nda_country: null,
      sections_to_process: getFilledSections(),
    //   holiday_maker_income: formData.isWorkingHolidayMaker ? {
    //     income_gross: Number(holidayMakerIncome.grossIncome) || null,
    //     income_tax_withheld: Number(holidayMakerIncome.taxWithheld) || null,
    //     income_deductions: Number(holidayMakerIncome.deductions) || null
    // } : null,

    whm_income_gross: Number(holidayMakerIncome.grossIncome) || null,
    whm_tax_withheld: Number(holidayMakerIncome.taxWithheld) || null,
    whm_deductions: Number(holidayMakerIncome.deductions) || null
,
      // Income and deductions
      income_gross: Number(incomeRecord.grossIncome) || null,
      income_tax_withheld: Number(incomeRecord.taxWithheld) || null,
      income_deductions: Number(incomeRecord.deductions) || null,
  
      // Income tests
      income_exempt_fringe: Number(incomeTests.fringeBenefits) || null,
      income_not_exempt_fringe: Number(incomeTests.fringeBenefitsNonExempt) || null,
      income_employer_super: Number(incomeTests.employerSuper) || null,
      income_investment_loss: Number(incomeTests.investmentLoss) || null,
      income_rental_loss: Number(incomeTests.rentalLoss) || null,
      income_personal_super: Number(incomeTests.personalSuper) || null,
      income_family_trust_tax: Number(incomeTests.familyTrustTax) || null,
      income_lump_sum_taxed: Number(incomeTests.superLumpSum) || null,
      income_foreign_employment: Number(incomeTests.foreignIncome) || null,
  
      // Spouse details
      spouse_taxable_income: Number(spouseDetails.taxableIncome) || null,
      spouse_exempt_fringe: Number(spouseDetails.fringeBenefits) || null,
      spouse_employer_super: Number(spouseDetails.employerSuper) || null,
      spouse_investment_loss: Number(spouseDetails.investmentLoss) || null,
      spouse_rental_loss: Number(spouseDetails.rentalLoss) || null,
      spouse_personal_super: Number(spouseDetails.personalSuper) || null,
      spouse_trust_income: Number(spouseDetails.trustIncome) || null,
      spouse_family_trust_tax: Number(spouseDetails.familyTrustTax) || null,
      spouse_lump_sum_taxed: Number(spouseDetails.superLumpSum) || null,
      spouse_foreign_employment: Number(spouseDetails.foreignIncome) || null,
  
      // Tax offsets
      offset_zone: Number(taxOffsets.zoneOverseasForces) || null,
      offset_life_assurance: Number(taxOffsets.lifeAssuranceBonuses) || null,
      offset_seniors: Number(taxOffsets.seniorsAndPensioners) || null,
      offset_australian_super: Number(taxOffsets.australianSuper) || null,
      offset_foreign_income: Number(taxOffsets.foreignIncome) || null,
      offset_spouse_super_contributions: Number(taxOffsets.spouseSuper) || null,
      offset_invalid_carer: Number(taxOffsets.invalidCarer) || null,
      offset_landcare: Number(taxOffsets.landcareWater) || null,
      offset_rental_affordability: Number(taxOffsets.nationalRental) || null,
      offset_esvclp: Number(taxOffsets.earlyStageVenture) || null,
      offset_early_stage_investor: Number(taxOffsets.earlyStageInvestor) || null,
      offset_other_refundable: Number(taxOffsets.otherRefundable) || null,
  
      // Other amounts
      claim_foreign_withholding: Number(otherAmounts.foreignCredits) || null,
      claim_losses_carried_forward: Number(otherAmounts.lossesCarriedForward) || null,
  
      // Medicare
      medicare_dependents: Number(medicareDetails.dependentChildren) || null,
      medicare_levy_amount: Number(medicareDetails.medicareLevyAmount) || null,
      medicare_has_phi: medicareDetails.hasPrivateInsurance,
      medicare_no_levy_days: Number(medicareDetails.mlsSurchargeDays) || null,
      medicare_phi_records: medicareDetails.hasPrivateInsurance === 'yes' ? phiRecords : []
    };

  //   const payload={
  //     "target_year": "2021-22",
  //     "residency_status": "full",
  //     "months_resident": null,
  //     "has_spouse": "no",
  //     "tax_item_positions": [
  //         3, // Assuming this is another tax item you need
  //         4  // **** Assuming 4 is the 1-based position of the WHM checkbox ****
  //     ],
  //     "is_nda_country": "no", // **** MUST BE "yes" or "no", NOT null, if WHM is selected ****
  //     "sections_to_process": [
  //         1,
  //         2, // Process WHM section
  //         3,
  //         5,
  //         6,
  //         7
  //     ],
  //     "whm_income_gross": 545,
  //     "whm_tax_withheld": 322,
  //     "whm_deductions": 454,
  //     "income_gross": 34,
  //     "income_tax_withheld": 76,
  //     "income_deductions": 4546,
  //     "income_exempt_fringe": 34576,
  //     "income_non_exempt_fringe": 89765, // Note: The service code uses 'income_not_exempt_fringe'
  //     "income_employer_super": 4567,
  //     "income_investment_loss": 9876,
  //     "income_rental_loss": 4567,
  //     "income_personal_super": 8976,
  //     "income_family_trust_tax": 5678,
  //     "income_lump_sum_taxed": 9876,
  //     "income_foreign_employment": 768,
  //     "spouse_taxable_income": null,
  //     "spouse_exempt_fringe": null,
  //     // "spouse_not_exempt_fringe": null, // Key used by service
  //     "spouse_employer_super": null,
  //     "spouse_investment_loss": null,
  //     "spouse_rental_loss": null,
  //     "spouse_personal_super": null,
  //     "spouse_trust_income": null,
  //     "spouse_family_trust_tax": null,
  //     "spouse_lump_sum_taxed": null,
  //     "spouse_foreign_employment": null,
  //     "offset_zone": 4567,
  //     "offset_life_assurance": 5768,
  //     "offset_seniors": 45678,
  //     "offset_australian_super": 234,
  //     "offset_foreign_income": 456,
  //     "offset_spouse_super_contributions": 4567,
  //     "offset_invalid_carer": 2345,
  //     "offset_landcare": 34567,
  //     "offset_rental_affordability": 34567,
  //     "offset_esvclp": 7654,
  //     "offset_early_stage_investor": 4567,
  //     "offset_other_refundable": 2345,
  //     "claim_foreign_withholding": 3456,
  //     "claim_losses_carried_forward": 234,
  //     "medicare_dependents": 55,
  //     "medicare_levy_amount": 43,
  //     "medicare_has_phi": "no",
  //     "medicare_no_levy_days": 45,
  //     "medicare_phi_records": []
  // }
  
    try {
      const response = await axios.post(
        API_BASE_URL + '/ato_calculator/income_tax_estimator',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: Number.POSITIVE_INFINITY, // 60 seconds timeout
        }
      );
  
      if (!response.statusText==='OK') {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      // const result = await response.json();
      // console.log('Tax calculation result:', result);
      setResult(response?.data?.summary_result );
      setLoading(false);
      // Handle the response - you might want to show the results in the UI
      
    } catch (error) {
      setLoading(false);
      setError("These was an error calculating the tax")
      console.error('Error calculating tax:', error);
      // Handle the error - show error message to user
    }
  };

  const renderField = (field) => {
    if (field.condition && !field.condition(formData)) return null;

    switch (field.type) {
      case "select":
        return (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>
            <select
              id={field.name}
              name={field.name}
              value={formData[field.name] || ""}
              onChange={handleChange}
              className="form-control"
            >
              {field.options.map((option, idx) => (
                <option key={idx} value={option === "" ? "" : option}>
                  {option === "" ? "Select" : option}
                </option>
              ))}
            </select>
          </div>
        );

      case "radio":
        return (
          <div key={field.name} className="form-group">
            <label>{field.label}</label>
            <div className="radio-group">
              {field.options.map((option) => (
                <div key={option.value} className="radio-option">
                  <input
                    type="radio"
                    id={`${field.name}-${option.value}`}
                    name={field.name}
                    value={option.value}
                    checked={formData[field.name] === option.value}
                    onChange={handleChange}
                  />
                  <label htmlFor={`${field.name}-${option.value}`}>
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case "checkboxGroup":
        return (
          <div key={field.label} className="form-group">
            <label>{field.label}</label>
            <div className="checkbox-group">
              {field.checkboxes.map((checkbox) => (
                <div key={checkbox.name} className="checkbox-option">
                  <input
                    type="checkbox"
                    id={checkbox.name}
                    name={checkbox.name}
                    checked={formData[checkbox.name]}
                    onChange={handleChange}
                  />
                  <label htmlFor={checkbox.name}>{checkbox.label}</label>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="form-container">
      <h1>Income tax estimator</h1>
      <p>
        This tool will help you estimate a tax refund or debt for an income
        year.
      </p>
      <p>All fields marked with * are mandatory</p>

      <form onSubmit={handleSubmit}>
        {formSchema.map(renderField)}

        <div className="income-section">
          <h2>Income and deductions</h2>
          <p>
            Enter the total amounts for your income, tax withheld and deductions
            that occurred in the selected income year.
          </p>

          {!isAddingIncome ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditIncome}
            >
              {hasSubmittedIncome ? "Edit Income Details" : "Add Income Details"}
            </button>
          ) : (
            <div className="income-record">
              <div className="form-group">
                <label>Total gross income payments you received *</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeRecord.grossIncome}
                    onChange={(e) =>
                      handleIncomeChange("grossIncome", e.target.value)
                    }
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Total amount of tax that was withheld</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeRecord.taxWithheld}
                    onChange={(e) =>
                      handleIncomeChange("taxWithheld", e.target.value)
                    }
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Total amount of deductions to claim</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeRecord.deductions}
                    onChange={(e) =>
                      handleIncomeChange("deductions", e.target.value)
                    }
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="submit-button"
                  onClick={handleSaveIncome}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingIncome(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedIncome && !isAddingIncome && (
            <div className="totals-section">
              <table>
                <thead>
                  <tr>
                    <th>Income</th>
                    <th>Tax withheld</th>
                    <th>Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${Number(incomeRecord.grossIncome || 0).toFixed(2)}</td>
                    <td>${Number(incomeRecord.taxWithheld || 0).toFixed(2)}</td>
                    <td>${Number(incomeRecord.deductions || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add this after the income-section div and before the income-tests-section div */}

{formData.nonDiscCountry==="yes" && (
  <div className="working-holiday-section">
    <h2>Working holiday maker income and deductions</h2>
    <p>Enter the total amounts for your income, tax withheld and deductions that occurred in the selected income year.</p>

    {!isAddingHolidayIncome ? (
      <button
        type="button"
        className="add-button"
        onClick={handleAddEditHolidayIncome}
      >
        {hasSubmittedHolidayIncome ? "Edit Holiday Income Details" : "Add Holiday Income Details"}
      </button>
    ) : (
      <div className="holiday-income-record">
        <div className="form-group">
          <label>Total gross income payments you received while you were a working holiday maker *</label>
          <div className="input-with-prefix">
            <span>$</span>
            <input
              type="number"
              value={holidayMakerIncome.grossIncome}
              onChange={(e) => handleHolidayIncomeChange("grossIncome", e.target.value)}
              step="0.01"
              required
            />
            <span>.00</span>
          </div>
        </div>

        <div className="form-group">
          <label>Total amount of tax that was withheld while you were a working holiday maker</label>
          <div className="input-with-prefix">
            <span>$</span>
            <input
              type="number"
              value={holidayMakerIncome.taxWithheld}
              onChange={(e) => handleHolidayIncomeChange("taxWithheld", e.target.value)}
              step="0.01"
            />
            <span>.00</span>
          </div>
        </div>

        <div className="form-group">
          <label>Total amount of deductions to claim while you were a working holiday maker</label>
          <div className="input-with-prefix">
            <span>$</span>
            <input
              type="number"
              value={holidayMakerIncome.deductions}
              onChange={(e) => handleHolidayIncomeChange("deductions", e.target.value)}
              step="0.01"
            />
            <span>.00</span>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="submit-button" onClick={handleSaveHolidayIncome}>
            Save
          </button>
          <button
            type="button"
            className="cancel-button"
            onClick={() => setIsAddingHolidayIncome(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    )}

    {hasSubmittedHolidayIncome && !isAddingHolidayIncome && (
      <div className="totals-section">
        <table>
          <thead>
            <tr>
              <th>Income</th>
              <th>Tax withheld</th>
              <th>Deductions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${Number(holidayMakerIncome.grossIncome || 0).toFixed(2)}</td>
              <td>${Number(holidayMakerIncome.taxWithheld || 0).toFixed(2)}</td>
              <td>${Number(holidayMakerIncome.deductions || 0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

        <div className="income-tests-section">
          <h2>Income tests</h2>
          <p>Enter the amounts below that relate to your other income.</p>

          {!isAddingTests ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditTests}
            >
              {hasSubmittedTests ? "Edit Income Tests" : "Add Income Tests"}
            </button>
          ) : (
            <div className="income-tests-record">
              <div className="form-group">
                <label>Your total reportable fringe benefits exempt</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.fringeBenefits}
                    onChange={(e) => handleTestsChange("fringeBenefits", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Your total reportable fringe benefits not exempt</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.fringeBenefitsNonExempt}
                    onChange={(e) => handleTestsChange("fringeBenefitsNonExempt", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your reportable employer superannuation contributions</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.employerSuper}
                    onChange={(e) => handleTestsChange("employerSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              {/* <div className="form-group">
                <label>Your reportable employer superannuation contributions</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.employerSuper}
                    onChange={(e) => handleTestsChange("employerSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div> */}

              <div className="form-group">
                <label>Your net financial investment loss</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.investmentLoss}
                    onChange={(e) => handleTestsChange("investmentLoss", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your net rental property loss</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.rentalLoss}
                    onChange={(e) => handleTestsChange("rentalLoss", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your deductible personal superannuation contributions</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.personalSuper}
                    onChange={(e) => handleTestsChange("personalSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your net amount on which family trust distribution tax has been paid</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.familyTrustTax}
                    onChange={(e) => handleTestsChange("familyTrustTax", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your superannuation lump sum taxed elements with a zero rate</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.superLumpSum}
                    onChange={(e) => handleTestsChange("superLumpSum", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your exempt foreign employment income</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={incomeTests.foreignIncome}
                    onChange={(e) => handleTestsChange("foreignIncome", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="submit-button" onClick={handleSaveTests}>
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingTests(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedTests && !isAddingTests && (
            <div className="totals-section">
              <table>
                <tbody>
                  <tr>
                    <td>Your total reportable fringe benefits</td>
                    <td>${Number(incomeTests.fringeBenefits || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your reportable employer superannuation contributions</td>
                    <td>${Number(incomeTests.employerSuper || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your net financial investment loss</td>
                    <td>${Number(incomeTests.investmentLoss || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your net rental property loss</td>
                    <td>${Number(incomeTests.rentalLoss || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your deductible personal superannuation contributions</td>
                    <td>${Number(incomeTests.personalSuper || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your net amount on which family trust distribution tax has been paid</td>
                    <td>${Number(incomeTests.familyTrustTax || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your superannuation lump sum taxed elements with a zero rate</td>
                    <td>${Number(incomeTests.superLumpSum || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your exempt foreign employment income</td>
                    <td>${Number(incomeTests.foreignIncome || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {formData.hadSpouse==="yes"&&(<div className="spouse-details-section">
          <h2>Spouse details</h2>
          <p>Enter the amounts below using annual amounts that relate to your spouse.</p>

          {!isAddingSpouse ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditSpouse}
            >
              {hasSubmittedSpouse ? "Edit Spouse Details" : "Add Spouse Details"}
            </button>
          ) : (
            <div className="spouse-details-record">
              <div className="form-group">
                <label>Your spouse's taxable income *</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.taxableIncome}
                    onChange={(e) => handleSpouseChange("taxableIncome", e.target.value)}
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's total reportable fringe benefits</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.fringeBenefits}
                    onChange={(e) => handleSpouseChange("fringeBenefits", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's reportable employer super contributions</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.employerSuper}
                    onChange={(e) => handleSpouseChange("employerSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's net financial investment loss</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.investmentLoss}
                    onChange={(e) => handleSpouseChange("investmentLoss", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's net rental property loss</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.rentalLoss}
                    onChange={(e) => handleSpouseChange("rentalLoss", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's deductible personal superannuation contributions</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.personalSuper}
                    onChange={(e) => handleSpouseChange("personalSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's share of trust income on which the trustee is assessed under section 98, and which has not been included in your spouse's taxable income</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.trustIncome}
                    onChange={(e) => handleSpouseChange("trustIncome", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's net amount on which family trust distribution tax has been paid</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.familyTrustTax}
                    onChange={(e) => handleSpouseChange("familyTrustTax", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's superannuation lump sum taxed elements with a zero rate</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.superLumpSum}
                    onChange={(e) => handleSpouseChange("superLumpSum", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Your spouse's exempt foreign employment income</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={spouseDetails.foreignIncome}
                    onChange={(e) => handleSpouseChange("foreignIncome", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="submit-button" onClick={handleSaveSpouse}>
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingSpouse(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedSpouse && !isAddingSpouse && (
            <div className="totals-section">
              <table>
                <tbody>
                  <tr>
                    <td>Your spouse's taxable income</td>
                    <td>${Number(spouseDetails.taxableIncome || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's total reportable fringe benefits</td>
                    <td>${Number(spouseDetails.fringeBenefits || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's reportable employer superannuation contributions</td>
                    <td>${Number(spouseDetails.employerSuper || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's net financial investment loss</td>
                    <td>${Number(spouseDetails.investmentLoss || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's net rental property loss</td>
                    <td>${Number(spouseDetails.rentalLoss || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's deductible personal superannuation contributions</td>
                    <td>${Number(spouseDetails.personalSuper || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's share of trust income</td>
                    <td>${Number(spouseDetails.trustIncome || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's net amount on which family trust distribution tax has been paid</td>
                    <td>${Number(spouseDetails.familyTrustTax || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's superannuation lump sum taxed elements with a zero rate</td>
                    <td>${Number(spouseDetails.superLumpSum || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Your spouse's exempt foreign employment income</td>
                    <td>${Number(spouseDetails.foreignIncome || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>)}

        <div className="tax-offsets-section">
          <h2>Tax offsets</h2>
          <p>Enter the amounts for the tax offsets you are claiming.</p>

          {!isAddingOffsets ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditOffsets}
            >
              {hasSubmittedOffsets ? "Edit Tax Offsets" : "Add Tax Offsets"}
            </button>
          ) : (
            <div className="tax-offsets-record">
              <div className="info-box">
                <h3>Zone or overseas forces calculator</h3>
                <p>You can use the Zone or overseas forces calculator to work out this amount and then enter it in the field below.</p>
              </div>
              <div className="form-group">
                <label>Zone or overseas forces offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.zoneOverseasForces}
                    onChange={(e) => handleOffsetsChange("zoneOverseasForces", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Life assurance bonuses offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.lifeAssuranceBonuses}
                    onChange={(e) => handleOffsetsChange("lifeAssuranceBonuses", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="info-box">
                <h3>Beneficiary tax offset and seniors and pensioners tax offset calculator</h3>
                <p>You can use the beneficiary tax offset and seniors and pensioners tax offset calculator to work out this amount and then enter it in the field below.</p>
              </div>
              <div className="form-group">
                <label>Seniors and pensioners (includes self-funded retirees) offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.seniorsAndPensioners}
                    onChange={(e) => handleOffsetsChange("seniorsAndPensioners", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Australian superannuation income stream offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.australianSuper}
                    onChange={(e) => handleOffsetsChange("australianSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Foreign income tax offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.foreignIncome}
                    onChange={(e) => handleOffsetsChange("foreignIncome", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="info-box">
                <h3>Invalid and invalid carer tax offset calculator</h3>
                <p>You can use the Invalid and invalid carer tax offset calculator to work out this amount and then enter it in the field below.</p>
              </div>
              <div className="form-group">
                <label>Invalid and invalid carer offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.invalidCarer}
                    onChange={(e) => handleOffsetsChange("invalidCarer", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Landcare and water facility offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.landcareWater}
                    onChange={(e) => handleOffsetsChange("landcareWater", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>National rental affordability scheme tax offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.nationalRental}
                    onChange={(e) => handleOffsetsChange("nationalRental", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Early stage venture capital limited partnership (ESVCLP) tax offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.earlyStageVenture}
                    onChange={(e) => handleOffsetsChange("earlyStageVenture", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Early stage investor tax offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.earlyStageInvestor}
                    onChange={(e) => handleOffsetsChange("earlyStageInvestor", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Other refundable offset amounts</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.otherRefundable}
                    onChange={(e) => handleOffsetsChange("otherRefundable", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Superannuation contributions you've paid on behalf of your spouse offset amount</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={taxOffsets.spouseSuper}
                    onChange={(e) => handleOffsetsChange("spouseSuper", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="submit-button" onClick={handleSaveOffsets}>
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingOffsets(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedOffsets && !isAddingOffsets && (
            <div className="totals-section">
              <table>
                <tbody>
                  <tr>
                    <td>Zone or overseas forces tax offset amount</td>
                    <td>${Number(taxOffsets.zoneOverseasForces || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Life assurance bonuses tax offset amount</td>
                    <td>${Number(taxOffsets.lifeAssuranceBonuses || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Seniors and Pensioners tax offset amount</td>
                    <td>${Number(taxOffsets.seniorsAndPensioners || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Australian superannuation income stream tax offset amount</td>
                    <td>${Number(taxOffsets.australianSuper || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Foreign income tax offset amount</td>
                    <td>${Number(taxOffsets.foreignIncome || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Invalid and invalid carer tax offset amount</td>
                    <td>${Number(taxOffsets.invalidCarer || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Landcare and water facility tax offset amount</td>
                    <td>${Number(taxOffsets.landcareWater || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>National rental affordability scheme tax offset amount</td>
                    <td>${Number(taxOffsets.nationalRental || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Early stage venture capital limited partnership tax offset amount</td>
                    <td>${Number(taxOffsets.earlyStageVenture || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Early stage investor tax offset amount</td>
                    <td>${Number(taxOffsets.earlyStageInvestor || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Other refundable tax offsets amount</td>
                    <td>${Number(taxOffsets.otherRefundable || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Superannuation contributions on behalf of your spouse tax offset amount</td>
                    <td>${Number(taxOffsets.spouseSuper || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="other-amounts-section">
          <h2>Other claimable amounts</h2>

          {!isAddingOther ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditOther}
            >
              {hasSubmittedOther ? "Edit Other Amounts" : "Add Other Amounts"}
            </button>
          ) : (
            <div className="other-amounts-record">
              <div className="form-group">
                <label>Foreign resident capital gains withholding credits</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={otherAmounts.foreignCredits}
                    onChange={(e) => handleOtherChange("foreignCredits", e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="info-link">
                  <a href="#">Read more about Foreign resident capital gains withholding</a>
                </div>
              </div>

              <div className="form-group">
                <label>Losses carried forward from previous years</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={otherAmounts.lossesCarriedForward}
                    onChange={(e) => handleOtherChange("lossesCarriedForward", e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="info-link">
                  <a href="#">Read more about Claiming tax losses from previous years</a>
                </div>
              </div>

              <div className="info-box">
                <h3>Credit for interest on early payments calculator</h3>
                <p>You can use the Credit for interest on early payments calculator to work out this amount and then enter it in the field below.</p>
              </div>
              <div className="form-group">
                <label>Amount for credit for interest on early payments</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={otherAmounts.earlyPaymentsCredit}
                    onChange={(e) => handleOtherChange("earlyPaymentsCredit", e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="submit-button" onClick={handleSaveOther}>
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingOther(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedOther && !isAddingOther && (
            <div className="totals-section">
              <table>
                <tbody>
                  <tr>
                    <td>Foreign resident capital gains withholding credits</td>
                    <td>${Number(otherAmounts.foreignCredits || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Losses carried forward from previous years</td>
                    <td>${Number(otherAmounts.lossesCarriedForward || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Credit for interest on early payments</td>
                    <td>${Number(otherAmounts.earlyPaymentsCredit || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="medicare-section">
          <h2>Medicare and private health insurance</h2>
          <p>Some taxpayers do not have to pay the Medicare levy, or pay a reduced amount. A Medicare levy reduction is based on your taxable income. A Medicare levy exemption is based on specific categories.</p>

          {!isAddingMedicare ? (
            <button
              type="button"
              className="add-button"
              onClick={handleAddEditMedicare}
            >
              {hasSubmittedMedicare ? "Edit Medicare Details" : "Add Medicare Details"}
            </button>
          ) : (
            <div className="medicare-record">
              <div className="form-group">
                <label>Enter the number of dependent children and students you have *</label>
                <input
                  type="number"
                  value={medicareDetails.dependentChildren}
                  onChange={(e) => handleMedicareChange("dependentChildren", e.target.value)}
                  min="0"
                  required
                />
              </div>

              <div className="info-box">
                <h3>Medicare levy calculator</h3>
                <p>You can use the Medicare levy calculator to work out this amount and then enter it in the field below.</p>
              </div>
              
              <div className="form-group">
                <label>Enter the Medicare levy amount you have to pay *</label>
                <div className="input-with-prefix">
                  <span>$</span>
                  <input
                    type="number"
                    value={medicareDetails.medicareLevyAmount}
                    onChange={(e) => handleMedicareChange("medicareLevyAmount", e.target.value)}
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Were you and all of your dependants covered by an appropriate level of private patient hospital cover? *</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      value="yes"
                      checked={medicareDetails.hasPrivateInsurance === "yes"}
                      onChange={(e) => handleMedicareChange("hasPrivateInsurance", e.target.value)}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="no"
                      checked={medicareDetails.hasPrivateInsurance === "no"}
                      onChange={(e) => handleMedicareChange("hasPrivateInsurance", e.target.value)}
                    />
                    No
                  </label>
                </div>
              </div>

              {medicareDetails.hasPrivateInsurance === "no" && (
                <div className="form-group">
                  <label>Enter the number of days you do not have to pay the Medicare levy surcharge *</label>
                  <input
                    type="number"
                    value={medicareDetails.mlsSurchargeDays}
                    onChange={(e) => handleMedicareChange("mlsSurchargeDays", e.target.value)}
                    min="0"
                    max="365"
                    required
                  />
                  <div className="info-text">
                    If you have to pay the surcharge for the whole period 1 July to 30 June of the selected income year enter 0.
                  </div>
                </div>
              )}

              {medicareDetails.hasPrivateInsurance === "yes" && (
                <div className="private-insurance-section">
                  <h3>Private health insurance</h3>
                  <p>Enter information about your private health insurance policies</p>
                  
                  {insurancePolicies.map((policy, index) => (
                    <div key={policy.id} className="policy-accordion">
                      <details>
                        <summary>Policy Record {index + 1}</summary>
                        <div className="policy-details">
                          <p>Premium: ${Number(policy.premium).toFixed(2)}</p>
                          <p>Rebate received: ${Number(policy.rebateReceived).toFixed(2)}</p>
                          <p>Benefit code: {policy.benefitCode}</p>
                          <p>Claiming for spouse: {policy.claimingForSpouse ? "Yes" : "No"}</p>
                          <div className="policy-actions">
                            <button onClick={() => handleEditPolicy(policy.id)}>Edit</button>
                            <button onClick={() => handleDeletePolicy(policy.id)}>Delete</button>
                          </div>
                        </div>
                      </details>
                    </div>
                  ))}

                  {showPolicyForm ? (
                    <div className="policy-form">
                      <div className="form-group">
                        <label>Your premiums eligible for Australian Government rebate *</label>
                        <div className="input-with-prefix">
                          <span>$</span>
                          <input
                            type="number"
                            value={currentPolicy.premium}
                            onChange={(e) => handlePolicyChange("premium", e.target.value)}
                            step="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Your Australian Government rebate received *</label>
                        <div className="input-with-prefix">
                          <span>$</span>
                          <input
                            type="number"
                            value={currentPolicy.rebateReceived}
                            onChange={(e) => handlePolicyChange("rebateReceived", e.target.value)}
                            step="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Benefit code *</label>
                        <select
                          value={currentPolicy.benefitCode}
                          onChange={(e) => handlePolicyChange("benefitCode", e.target.value)}
                          required
                        >
                          <option value="">- Select -</option>
                          <option value="30">30</option>
                          <option value="31">31</option>
                          <option value="32">32</option>
                          <option value="33">33</option>
                          <option value="34">34</option>
                          <option value="35">35</option>
                          <option value="36">36</option>
                          <option value="37">37</option>
                          <option value="38">38</option>
                          <option value="39">39</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={currentPolicy.claimingForSpouse}
                            onChange={(e) => handlePolicyChange("claimingForSpouse", e.target.checked)}
                          />
                          Claiming for spouse
                        </label>
                      </div>

                      <div className="form-actions">
                        <button type="button" className="submit-button" onClick={handleSavePolicy}>
                          Save Policy
                        </button>
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={() => setShowPolicyForm(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="add-button" onClick={handleAddPolicy}>
                      Add Policy Record
                    </button>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="submit-button" onClick={handleSaveMedicare}>
                  Save
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddingMedicare(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {hasSubmittedMedicare && !isAddingMedicare && (
            <div className="totals-section">
              <table>
                <tbody>
                  <tr>
                    <td>The Medicare levy amount you have to pay</td>
                    <td>${Number(medicareDetails.medicareLevyAmount || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Were you covered by private patient hospital cover?</td>
                    <td>{medicareDetails.hasPrivateInsurance === 'yes' ? 'Yes' : 'No'}</td>
                  </tr>
                  {medicareDetails.hasPrivateInsurance === 'no' && (
                    <tr>
                      <td>Days without Medicare levy surcharge</td>
                      <td>{medicareDetails.mlsSurchargeDays}</td>
                    </tr>
                  )}
                  {medicareDetails.hasPrivateInsurance === 'yes' && insurancePolicies.length > 0 && (
                    <tr>
                      <td colSpan="2">
                        <strong>Private Health Insurance Policies: {insurancePolicies.length}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {error && (
    <div className='error'>
      <p>{error}</p>
    </div>
  )}

  {result && (
    <div className='result-container'>
      <h2>Calculation Results</h2>
      <div className="result-summary">
        <p>{result}</p>
        {/* Add more result fields as needed */}
      </div>
    </div>
  )}

  {loading && (
    <div className='loading-container'>
      <div className='spinner'></div>
      <p>Calculating your home office expenses...</p>
    </div>
  )}
  <button type="submit" className="submit-button" disabled={loading}>
    {loading ? 'Calculating...' : 'Calculate Tax Estimate'}
  </button>
      </form>
    </div>
  );
};

export default IncomeTaxCalculator;
